import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Menu', path: '/menu' },
    { name: 'Order Online', path: '/order' },
    { name: 'Reserve a Table', path: '/reserve' },
  ];

  const aboutLinks = [
    { name: 'Our Story', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms & Conditions', path: '/terms' },
  ];

  return (
    <footer
      className="bg-[#1A2E1F] relative"
      data-testid="footer"
      itemScope
      itemType="https://schema.org/Restaurant"
    >
      {/* Top border accent */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column with NAP */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block">
              <h3 className="font-serif text-3xl text-[#C9A84C] mb-4" itemProp="name">
                It's Lahore
              </h3>
            </Link>
            <p className="text-[#E8DCC8]/80 text-sm leading-relaxed mb-6">
              Authentic cuisine, crafted with generations of flavour.
              The soul of Lahore, in the heart of Solihull.
            </p>

            {/* NAP */}
            <address
              className="not-italic text-[#E8DCC8] text-sm space-y-1 mb-6"
              itemProp="address"
              itemScope
              itemType="https://schema.org/PostalAddress"
            >
              <p className="font-semibold text-[#F5F0E8]">It's Lahore</p>
              <p>
                <span itemProp="streetAddress">44 Station Road</span>, <span itemProp="addressLocality">Solihull</span>, <span itemProp="postalCode">B91 3RX</span>
              </p>
              <p>
                Tel: <a href="tel:+01217254162" className="text-[#C9A84C] hover:underline" itemProp="telephone">0121 725 4162</a>
              </p>
              <p>
                Email: <a href="mailto:info@itslahore.co.uk" className="text-[#C9A84C] hover:underline" itemProp="email">info@itslahore.co.uk</a>
              </p>
            </address>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0D0D0D] transition-all duration-300"
                aria-label="Follow It's Lahore on Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0D0D0D] transition-all duration-300"
                aria-label="Follow It's Lahore on Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://tripadvisor.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tripadvisor"
                className="w-10 h-10 rounded-full border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] hover:bg-[#C9A84C] hover:text-[#0D0D0D] transition-all duration-300 font-bold text-xs"
                aria-label="Review It's Lahore on TripAdvisor"
              >
                TA
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg text-[#F5F0E8] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#E8DCC8]/70 hover:text-[#C9A84C] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h4 className="font-serif text-lg text-[#F5F0E8] mb-6">About Us</h4>
            <ul className="space-y-3">
              {aboutLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#E8DCC8]/70 hover:text-[#C9A84C] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Legal Links */}
            <h4 className="font-serif text-lg text-[#F5F0E8] mt-8 mb-4">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#E8DCC8]/70 hover:text-[#C9A84C] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-serif text-lg text-[#F5F0E8] mb-6">Opening Hours</h4>
            <div className="space-y-3">
              <div itemProp="openingHoursSpecification" itemScope itemType="https://schema.org/OpeningHoursSpecification">
                <meta itemProp="dayOfWeek" content="Monday" />
                <meta itemProp="dayOfWeek" content="Tuesday" />
                <meta itemProp="dayOfWeek" content="Wednesday" />
                <meta itemProp="dayOfWeek" content="Thursday" />
                <meta itemProp="dayOfWeek" content="Saturday" />
                <meta itemProp="dayOfWeek" content="Sunday" />
                <meta itemProp="opens" content="11:00" />
                <meta itemProp="closes" content="23:00" />
                <div className="flex justify-between items-center">
                  <p className="text-[#E8DCC8]/70 text-sm">Mon–Thu & Sat–Sun</p>
                  <p className="text-[#C9A84C] font-medium text-sm">11am – 11pm</p>
                </div>
              </div>
              <div itemProp="openingHoursSpecification" itemScope itemType="https://schema.org/OpeningHoursSpecification">
                <meta itemProp="dayOfWeek" content="Friday" />
                <meta itemProp="opens" content="14:00" />
                <meta itemProp="closes" content="23:00" />
                <div className="flex justify-between items-center">
                  <p className="text-[#E8DCC8]/70 text-sm">Friday</p>
                  <p className="text-[#C9A84C] font-medium text-sm">2pm – 11pm</p>
                </div>
              </div>
              <div className="pt-2 border-t border-[#C9A84C]/10">
                <p className="text-[#E8DCC8]/40 text-xs italic">Walk-ins always welcome</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#0D0D0D]/50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#E8DCC8]/50 text-sm">
              © {currentYear} It's Lahore. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-sm text-[#E8DCC8]/50">
              <span>100% Halal Certified</span>
              <span className="text-[#C9A84C]">•</span>
              <span>Family Owned Since 2012</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
